import { BuildClass, Do } from '../../../universal';
import { React } from '../../lib';
import { Alerts } from '../component-main';
import { reactRender, unmountReactOnElement } from '../component-react';
import { svrtsRequest } from '../svrts-interface';
import { Button } from './buttons';
import { LoadingSpinnerSmall } from './loading';
import { CJSX } from './meta-types';
import { Modal } from './modal';
import { stubCanvas } from './stubs';
import { Textarea } from './textbox';
const theme = {
    MainText: 'text-slate-900',
    TextboxBG: 'bg-neutral-100',
    TextboxBorder: 'border-neutral-100',
    TextboxBorderFocus: 'focus:border-neutral-100',
    SendButtonBG: 'bg-blue-700',
    SendButtonHoverBG: 'hover:bg-blue-800',
    RecordButtonBG: 'bg-red-500',
    RecordButtonHoverBG: 'hover:bg-red-600',
    TextboxBGRGB: '#ffffff',
    TextboxWaveformStroke: '#171717',
};
const SECONDS_TO_SHOW_WAVE = 1;
const AUDIO_FFT_SIZE = 2048;
const EXP_SCALE = 0.5;
export const startGlobalVoiceRecording = (input) => {
    const el = document.createElement('div');
    el.id = 'global-voice-wrapper';
    document.body.appendChild(el);
    reactRender(el, React.createElement(GlobalVoiceRecording, { ...input, container: el })).catch(err => {
        console.error(err);
    });
};
const focusOriginalOrSubmitButton = (originatingFormElement) => {
    const submitButton = originatingFormElement.querySelector('button[type="submit"]');
    if (submitButton) {
        submitButton.focus();
        return;
    }
    originatingFormElement.focus();
};
const GlobalVoiceRecording = (props) => {
    const [mountCount, setMountCount] = React.useState(0);
    const [hasCancelled, setHasCancelled] = React.useState(false);
    const [isRendering, setIsRendering] = React.useState(true);
    const removeSelf = React.useCallback(() => {
        focusOriginalOrSubmitButton(props.originatingFormElement);
        setIsRendering(false);
    }, [setIsRendering]);
    React.useEffect(() => {
        if (!isRendering) {
            unmountReactOnElement(props.container);
            requestAnimationFrame(() => {
                props.container.remove();
            });
        }
    }, [props.container, isRendering]);
    React.useEffect(() => {
        if (hasCancelled && mountCount == 0) {
            removeSelf();
        }
    }, [hasCancelled, mountCount, removeSelf]);
    const [transcribing, setTranscribing] = React.useState(false);
    const [text, setText] = React.useState(null);
    const [sending, setSending] = React.useState(false);
    const sendResult = (txt) => {
        setSending(true);
        if (!isRendering) {
            return;
        }
        props.onTranscribed(txt, result => {
            if (!isRendering) {
                return;
            }
            if (result !== true) {
                Alerts.Alert({ msg: result });
            }
            removeSelf();
        });
    };
    const formRect = props.originatingFormElement.getBoundingClientRect();
    return (isRendering && (React.createElement(Modal, null,
        React.createElement("div", { className: "tailwind-wrapper", style: {
                height: '100%',
                width: '100%',
                background: 'rgba(0,0,0,0.1)',
            } },
            React.createElement("div", { className: BuildClass({
                    'relative  z-20': true,
                    'bg-white border-neutral-600': true,
                    'max-w-[500px] mx-auto my-2 p-1 rounded-md': true,
                    'shadow-md drop-shadow-md': true,
                }) },
                React.createElement(VoiceRecorder, { autoStart: true, prompt: props.prompt ?? null, onSend: v => {
                        setTranscribing(false);
                        setText(v);
                        if (!props.editBeforeSending) {
                            sendResult(v ?? '');
                        }
                    }, onTranscribingStart: () => {
                        setTranscribing(true);
                    }, onMount: () => {
                        setMountCount(v => v + 1);
                    }, onCancel: () => {
                        setMountCount(v => v - 1);
                        setHasCancelled(true);
                    } }),
                transcribing && (React.createElement("div", { className: "m1 text-center py-1" },
                    "Transcribing... ",
                    React.createElement(LoadingSpinnerSmall, null),
                    React.createElement("a", { className: "cursor-pointer text-blue-600 hover:text-red-600 ml-2", onClick: e => {
                            e.preventDefault();
                            removeSelf();
                        } }, "[cancel]"))),
                !transcribing && text != null && (React.createElement(React.Fragment, null,
                    React.createElement(Textarea, { value: text, onUpdate: setText, autoExpand: true, placeholder: "Transcribed text goes here...", className: "w-full mt-1" }),
                    sending ? (React.createElement("div", { className: "m1 text-center py-1" },
                        "Processing... ",
                        React.createElement(LoadingSpinnerSmall, null),
                        React.createElement("a", { className: "cursor-pointer text-blue-600 hover:text-red-600 ml-2", onClick: e => {
                                e.preventDefault();
                                removeSelf();
                            } }, "[cancel]"))) : (React.createElement(Button, { type: "submit", className: "w-[calc(100%-8px)] m-1", lbl: "Submit", onClick: () => {
                            sendResult(text ?? '');
                        } }))))),
            React.createElement("div", { className: "border-dashed border-4 border-red-600 fixed z-10", style: {
                    top: `${formRect.top - 2}px`,
                    left: `${formRect.left - 2}px`,
                    width: `${formRect.width + 4}px`,
                    height: `${formRect.height + 4}px`,
                } })))));
};
export const VoiceRecorder = (props) => {
    const elWaveform = React.useRef(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const audioState = React.useRef({
        audioChunks: [],
        mediaRec: null,
        analyser: null,
    });
    const [, setAudioStateCounter] = React.useState(0);
    const startRecording = React.useCallback(async () => {
        await startRecordingWrapper({
            audioState: audioState.current,
            elWaveform: elWaveform.current ?? stubCanvas,
        });
        audioState.current.audioChunks = [];
        setAudioStateCounter(s => s + 1);
    }, [audioState, setAudioStateCounter, elWaveform]);
    const stopRecording = React.useCallback((send) => {
        stopRecordingWrapper({
            audioState: audioState.current,
            onUpdate: voice => {
                props.onTranscribingStart?.();
                sendTranscriptionRequest({
                    voice: voice,
                    prompt: props.prompt,
                    setIsLoading: setIsLoading,
                    onComplete: text => {
                        props.onSend(text);
                    },
                }).catch(err => {
                    console.error(err);
                });
            },
            send: send,
        });
        audioState.current.mediaRec = null;
        setAudioStateCounter(s => s + 1);
        if (!send) {
            requestAnimationFrame(() => {
                props.onCancel?.();
            });
        }
    }, [props, audioState, setAudioStateCounter]);
    React.useEffect(() => {
        defineGlobalKeybinds(startRecording, stopRecording);
    }, [startRecording, stopRecording]);
    React.useEffect(() => {
        props.onMount?.();
        if (!props.autoStart) {
            return () => { };
        }
        const prm = startRecording().catch(err => {
            console.error(err);
        });
        return () => {
            prm.then(() => {
                stopRecording(false);
            }).catch(err => {
                console.error(err);
            });
        };
    }, []);
    return (React.createElement("div", { className: BuildClass(['tailwind-wrapper', props.className]) },
        React.createElement("div", { className: "w-full z-10 flex shadow-md" },
            React.createElement("div", { className: BuildClass({
                    'flex-grow box-border border rounded-l-md border-r-0': true,
                    [theme.TextboxBG]: true,
                    [theme.TextboxBorder]: true,
                }) },
                React.createElement("canvas", { ref: elWaveform, className: BuildClass({
                        'w-full h-10': true,
                        hidden: isLoading || !audioState.current.mediaRec,
                    }) })),
            React.createElement(CJSX, { cond: !audioState.current.mediaRec },
                React.createElement("button", { className: BuildClass({
                        'px-4 py-2 rounded-r-md': true,
                        [theme.MainText]: true,
                        [theme.SendButtonBG]: true,
                        [theme.SendButtonHoverBG]: true,
                    }), title: "Click to record a voice message to send, instead of typing", onClick: () => {
                        startRecording().catch(err => {
                            console.error(err);
                        });
                    } },
                    React.createElement("img", { alt: "Voice Message", src: "/static/img/i8/ios16-microphone.svg", className: "w-6 h-6 invert" }))),
            React.createElement(CJSX, { cond: Boolean(audioState.current.mediaRec) },
                React.createElement(Button, { type: "ui5-borderless-24", img: "/static/img/i8/ios16-cross.svg", style: { padding: '0 4px' }, className: `px-2 py-2 ${theme.MainText} ${theme.SendButtonBG} ${theme.SendButtonHoverBG}`, title: "Click to cancel your voice recording without sending", onClick: () => {
                        stopRecording(false);
                    } }),
                React.createElement(Button, { type: "ui5-borderless-24", img: "/static/img/i8/ios16-tick.svg", autoFocus: true, style: { padding: '0 4px' }, className: `px-2 py-2 ${theme.MainText} rounded-r-md ${theme.RecordButtonBG} ${theme.RecordButtonHoverBG}`, title: "Click to send your voice recording", onClick: () => {
                        stopRecording(true);
                    } })))));
};
const sendTranscriptionRequest = async (input) => {
    input.setIsLoading(true);
    const audioBuffer = await input.voice.arrayBuffer();
    const audioBase64 = Buffer.from(audioBuffer).toString('base64');
    svrtsRequest({
        data: {
            funcID: [0, 51],
            audio: audioBase64,
            prompt: input.prompt,
        },
        any: () => {
            input.setIsLoading(false);
        },
        no: data => {
            Alerts.Alert({ msg: data.message });
        },
        yes: data => {
            input.onComplete(data.text);
        },
    });
};
const startRecordingWrapper = async (input) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream, {
        audioBitsPerSecond: 64000,
        mimeType: 'audio/webm',
    });
    input.audioState.mediaRec = recorder;
    const audioContext = new window.AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = AUDIO_FFT_SIZE;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    input.audioState.analyser = analyser;
    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    const canvas = input.elWaveform;
    const historySize = audioContext.sampleRate * SECONDS_TO_SHOW_WAVE;
    const historyBuffer = new Uint8Array(historySize).fill(128);
    const canvasContext = canvas?.getContext('2d');
    if (canvas && canvasContext) {
        const draw = () => {
            analyser.getByteTimeDomainData(dataArray);
            historyBuffer.set(dataArray, historyBuffer.length - bufferLength);
            historyBuffer.copyWithin(0, bufferLength);
            canvasContext.fillStyle = theme.TextboxBGRGB;
            canvasContext.fillRect(0, 0, canvas.width, canvas.height);
            canvasContext.lineWidth = 1;
            canvasContext.strokeStyle = theme.TextboxWaveformStroke;
            canvasContext.beginPath();
            let x = 0;
            const sliceWidth = (canvas.width * 1.0) / historySize;
            for (let i = 0; i < historySize; i++) {
                const v1 = historyBuffer[i] ?? 128;
                const v2 = v1 / 128.0 - 1;
                const vScaled = Math.abs(v2) ** EXP_SCALE * Math.sign(v2);
                const y = ((vScaled + 1) * canvas.height) / 2;
                if (i === 0) {
                    canvasContext.moveTo(x, y);
                }
                else {
                    canvasContext.lineTo(x, y);
                }
                x += sliceWidth;
            }
            canvasContext.lineTo(canvas.width, canvas.height / 2);
            canvasContext.stroke();
            if (input.audioState.mediaRec) {
                requestAnimationFrame(draw);
            }
        };
        draw();
    }
    recorder.addEventListener('dataavailable', event => {
        input.audioState.audioChunks.push(event.data);
    });
    recorder.start();
};
const stopRecordingWrapper = (input) => {
    const mediaRec = input.audioState.mediaRec;
    if (!mediaRec) {
        return;
    }
    if (input.send) {
        mediaRec.addEventListener('stop', () => {
            Do(async () => {
                if (input.audioState.audioChunks.length == 0) {
                    console.error('No audio chunks available');
                    return;
                }
                const duration = await getAudioDuration(input.audioState.audioChunks);
                if (duration < 1) {
                    console.log('Skipping sending voice clip - length < 1: ', duration);
                    input.audioState.audioChunks = [];
                    return;
                }
                const audio_blob = new Blob(input.audioState.audioChunks);
                input.onUpdate(audio_blob);
                input.audioState.audioChunks = [];
            }).catch(err => {
                console.error(err);
            });
        });
    }
    mediaRec.stop();
    mediaRec.stream.getTracks().forEach(track => {
        track.stop();
    });
    input.audioState.analyser?.disconnect();
};
const defineGlobalKeybinds = (startRecording, stopRecording) => {
    const pttKeyDown = (ev) => {
        if (ev.code === 'ControlRight' && !ev.repeat) {
            startRecording().catch(err => {
                console.error(err);
            });
        }
    };
    const pttKeyUp = (ev) => {
        if (ev.code === 'ControlRight') {
            stopRecording(true);
        }
    };
    document.addEventListener('keydown', pttKeyDown);
    document.addEventListener('keyup', pttKeyUp);
    return () => {
        document.removeEventListener('keydown', pttKeyDown);
        document.removeEventListener('keyup', pttKeyUp);
    };
};
const getAudioDuration = async (chunks) => new Promise(resolve => {
    const audioBlob = new Blob(chunks);
    const audioURL = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioURL);
    audio.onloadedmetadata = () => {
        console.log(audio.duration);
        resolve(audio.duration);
    };
});
//# sourceMappingURL=voice-recorder.js.map